import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

/**
 * KpiCard — a single KPI tile.
 *
 * Props:
 *  id        string    data-testid suffix
 *  icon      ReactNode lucide icon element
 *  label     string    card heading
 *  value     number|null  main value (null → "–" + "Ej tillgänglig")
 *  unit      string?   optional unit suffix (e.g. "%")
 *  trend     number?   change vs previous period (positive = up)
 *  loading   bool
 */
const KpiCard = ({ id, icon, label, value, unit = '', trend, loading = false }) => {
  const hasValue = value !== null && value !== undefined;

  const TrendIcon = trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : Minus;
  const trendColor = trend > 0
    ? 'text-green-600'
    : trend < 0
      ? 'text-red-500'
      : 'text-muted-foreground';

  return (
    <Card data-testid={`kpi-${id}`} role="article" className="hover:shadow-md transition-shadow">
      <CardContent className="p-5 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            {label}
          </span>
          <span className="text-muted-foreground/60" aria-hidden="true">{icon}</span>
        </div>

        {loading ? (
          <div className="space-y-2" data-testid={`kpi-${id}-loading`}>
            <div className="h-8 w-24 bg-muted rounded animate-pulse" />
            <div className="h-3 w-16 bg-muted rounded animate-pulse" />
          </div>
        ) : (
          <>
            <p
              className="text-3xl font-heading font-black"
              style={{ color: '#333333' }}
              data-testid={`kpi-${id}-value`}
            >
              {hasValue ? `${value}${unit}` : '–'}
            </p>

            {!hasValue && (
              <p className="text-xs text-muted-foreground" data-testid={`kpi-${id}-unavailable`}>
                Ej tillgänglig
              </p>
            )}

            {hasValue && trend !== undefined && trend !== null && (
              <div className={`flex items-center gap-1 text-xs font-medium ${trendColor}`}
                data-testid={`kpi-${id}-trend`}
              >
                <TrendIcon size={13} aria-hidden="true" />
                <span>
                  {trend > 0 ? '+' : ''}{trend}{unit} vs föregående period
                </span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default KpiCard;
